import { LoadingScreen } from '@components/LoadingScreen';
import { GoogleLoginButton, MicrosoftLoginButton } from '@components/LoginButtons';
import { LocaleToggle } from '@modules/application/components/LocaleToggle';
import { TenantBrandingHeader } from '@modules/tenants/components/TenantBrandingHeader';
import { TenantThemeContext } from '@modules/tenants/components/TenantThemeContextProvider';
import { Button, Container, Divider, Paper, Stack, TextField, Typography } from '@mui/material';
import LoginImage from '@assets/images/login.jpg';
import { useGetMe } from '@utils/useGetMe';
import { useGetLoginInfoQuery } from 'gql/index';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import { useNotification } from '../../../utils/useNotification';
import { useAuthResultMessageFormatter } from '../../../utils/getAuthResultMessage';
import { displayTenantSwitcherAfterLoginState } from '../atoms';
import { useResponsive } from '@utils/useResponsive';

export const TenantLoginPage: React.FC = () => {
  const { formatMessage } = useIntl();
  const formatAuthResultMessage = useAuthResultMessageFormatter();
  const [loggingInProvider, setLoggingInProvider] = useState<string>();
  const setDisplayTenantSwitcherAfterLoginState = useSetAtom(displayTenantSwitcherAfterLoginState);
  const [params, setParams] = useSearchParams();
  const { tenantId } = useParams();
  const [returnUrl, setReturnUrl] = useState<string>(`/${tenantId}`);
  const tenantContext = useContext(TenantThemeContext);
  const navigate = useNavigate();
  

  const { notifyFailure, notifySuccess } = useNotification();

  const { me, isFetching, isFetched } = useGetMe();
  const { data: loginInfo, isFetching: isFetchingLoginInfo } = useGetLoginInfoQuery(undefined, { select: d => d.loginInfo });

  const { isMobile } = useResponsive();
  useEffect(() => {
    const returnUrlParam = params.get('returnUrl');

    if (returnUrlParam) {
      setParams((prevParams) => {
        prevParams.delete('returnUrl');
        return prevParams;
      });

      setReturnUrl(returnUrlParam);
    }
  }, [isFetched, me, navigate, notifyFailure, params, returnUrl, setDisplayTenantSwitcherAfterLoginState, setParams, tenantContext.isBrandingEnabled]);

  useEffect(() => {
    const successParam = params.get('success');
    const code = params.get('code');
    const hasResult = successParam === 'true' || code != null;
    if (hasResult) {
      const success = successParam === 'true';
      const message = formatAuthResultMessage(success, code);
      if (message) {
        if (success) notifySuccess(message);
        else notifyFailure(message);
      }
      setParams((prevParams) => {
        prevParams.delete('success');
        prevParams.delete('code');
        return prevParams;
      });
    }
  }, []);

  const isSignedInWithMagicLink = useMemo(() => loginInfo?.isAuthenticatedWithMagicLink, [loginInfo]);
  const prefilledEmail = useMemo(() => isSignedInWithMagicLink && me?.email ? me.email : '', [isSignedInWithMagicLink, me?.email]);
  const passwordInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (prefilledEmail && passwordInputRef.current) {
      passwordInputRef.current.focus();
    }
  }, [prefilledEmail]);


  if (isFetching || isFetchingLoginInfo) {
    return <LoadingScreen />;
  }

  
  if (me != null && !isSignedInWithMagicLink && !isFetchingLoginInfo) {
    return <Navigate to={`/${tenantId}`} />;
  }

  return <Stack
    sx={{
      backgroundImage: `url(${ tenantContext.isBrandingEnabled ? tenantContext.loginBackgroundUrl ?? LoginImage : LoginImage})`,
      backgroundPosition: 'center center',
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'cover',
      flexGrow: 1
    }}
    flex={1}
    height={'100%'}
    justifyContent={'center'}
    alignItems={'center'}>
    

    <Container maxWidth="sm"  disableGutters sx={{ height: { xs: '100%', sm: 'auto' } }}> 
    <Paper elevation={isMobile ? 0 : 4}
    sx={{ 
      height: { xs: '100%', sm: 'auto' }, 
      width: { xs: '100%', sm: 'auto' },
    }}
    >
      <Stack p={4} gap={4} justifyContent="center" alignItems="center">
        <Stack direction="row" alignSelf="stretch" justifyContent="flex-end">
          <LocaleToggle />
        </Stack>
        <TenantBrandingHeader />

        <Stack spacing={1} alignItems="center" justifyContent="center">
          <Typography variant='h3' color='primary.main'>
            {formatMessage({ id: 'Welcome' })}
          </Typography>

          <Typography textAlign='center'>
            {formatMessage({ id: 'Please identify yourself to access the application' })}
          </Typography>
        </Stack>

        <Stack flex={1} spacing={2} alignItems='stretch' width="100%">
          <form id="external-account" action="/api/Account/ExternalLogin" method="post" style={{ width: '100%' }}>
            {returnUrl && <input name="returnUrl" type="hidden" value={returnUrl} />}
            <MicrosoftLoginButton
              loading={loggingInProvider === 'OpenIdConnect'}
              disabled={Boolean(loggingInProvider) && loggingInProvider !== 'OpenIdConnect'}
              fullWidth
              type="submit"
              name="provider"
              value="OpenIdConnect"
              onClickCapture={() => setLoggingInProvider('OpenIdConnect')}
            />
          </form>
          <form id="external-account-google" action="/api/Account/ExternalLogin" method="post" style={{ width: '100%' }}>
            {returnUrl && <input name="returnUrl" type="hidden" value={returnUrl} />}
            <GoogleLoginButton
              fullWidth
              loading={loggingInProvider === 'Google'}
              disabled={Boolean(loggingInProvider) && loggingInProvider !== 'Google'}
              type="submit"
              name="provider"
              value="Google"
              onClickCapture={() => setLoggingInProvider('Google')}
            />
          </form>
          <Divider sx={{ width: '100%' }}>{formatMessage({ id: 'or' })}</Divider>
          {tenantId != null && (
            <Stack spacing={2} width="100%" component="form" action={`/${tenantId}/api/Auth/Login`} method="post">
              {returnUrl && <input name="returnUrl" type="hidden" value={returnUrl} />}
              <TextField
                required
                fullWidth
                name="email"
                type="email"
                label={formatMessage({ id: 'Email' })}
                autoComplete="email"
                defaultValue={prefilledEmail}
              />
              <TextField
                required
                fullWidth
                name="password"
                type="password"
                label={formatMessage({ id: 'Password' })}
                autoComplete="current-password"
                inputRef={passwordInputRef}
              />
              <Button type="submit" variant="contained" fullWidth>
                {formatMessage({ id: 'Sign in' })}
              </Button>
              <Link to={`/${tenantId}/forgot-password`} style={{ alignSelf: 'center' }}>
                <Button type="button" variant="text" size="small">
                  {formatMessage({ id: 'Forgot password?' })}
                </Button>
              </Link>
            </Stack>
          )}
        </Stack>
      </Stack>
      </Paper>
    
    </Container>
  </Stack>;
};